import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { pageColors } from "../mock";
import MembersMap from "../components/MembersMap";
import InviteFriends from "../components/InviteFriends";
import { MyBookingFee } from "../components/MyBookingFee";
import { PaidGigs } from "../components/PaidGigs";
import {
  UserPlus,
  LogIn,
  LogOut,
  MessageSquare,
  Newspaper,
  Users,
  Download,
  KeyRound,
  ArrowLeft,
  Mail,
  Shirt,
  CalendarDays,
} from "lucide-react";

const AVATAR_MAX = 800 * 1024;
const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What street did you grow up on?",
  "What is your mother's maiden name?",
  "What was the model of your first car?",
  "What was the name of your elementary school?",
  "What is your favorite album of all time?",
];

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });


const Members = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    users,
    current,
    register,
    login,
    logout,
    getSecurityQuestion,
    resetPassword,
    lookupInvite,
    requestMemberCountAccess,
    resetWithToken,
  } = useAuth();

  const [mode, setMode] = useState("signup"); // signup | login | forgot
  const [resetToken] = useState(searchParams.get("reset_token") || "");
  const [rtPass, setRtPass] = useState("");
  const [rtPass2, setRtPass2] = useState("");
  const [rtErr, setRtErr] = useState("");
  const [rtBusy, setRtBusy] = useState(false);
  const [forgotLinkState, setForgotLinkState] = useState("idle"); // idle | busy | sent

  const emailResetLink = async () => {
    const email = forgot.email.trim().toLowerCase();
    if (!email || !email.includes("@")) { setErr("Enter your email above first."); return; }
    setErr("");
    setForgotLinkState("busy");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-reset-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email }),
      });
    } catch { /* always show the same confirmation */ }
    setForgotLinkState("sent");
  };

  const onTokenReset = async (e) => {
    e.preventDefault();
    setRtErr("");
    if (rtPass.length < 6) { setRtErr("Password must be at least 6 characters."); return; }
    if (rtPass !== rtPass2) { setRtErr("Passwords don't match."); return; }
    setRtBusy(true);
    const r = await resetWithToken({ token: resetToken, new_password: rtPass });
    setRtBusy(false);
    if (!r.ok) { setRtErr(r.error || "That reset link didn't work — ask for a new one."); return; }
    const next = new URLSearchParams(searchParams);
    next.delete("reset_token");
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  React.useEffect(() => {
    const slug = searchParams.get("availability");
    if (slug) navigate(`/availability?project=${slug}`, { replace: true });
  }, [searchParams, navigate]);

  const [inviteCode, setInviteCode] = useState(searchParams.get("invite") || "");
  const [inviteInfo, setInviteInfo] = useState(null); // { valid, inviter_name, inviter_email } | { error }
  const [highlights, setHighlights] = useState({});
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    expertise: "",
    intro: "",
    location: "",
    bio: "",
    website: "",
    instagram: "",
    twitter: "",
    phone: "",
    avatar: null,
    security_question: SECURITY_QUESTIONS[0],
    security_answer: "",
  });
  const [forgot, setForgot] = useState({
    email: "",
    question: "",
    answer: "",
    new_password: "",
    step: "email", // email -> answer -> done
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    document.title = "TMM|MEMBERS";
  }, []);

  // Fetch portfolio highlights (main image per member) for the grid.
  React.useEffect(() => {
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${API}/api/portfolios/highlights`);
        const d = await r.json();
        setHighlights(d.highlights || {});
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Validate the invite code as soon as we land on /members?invite=...
  React.useEffect(() => {
    let stop = false;
    (async () => {
      if (!inviteCode) {
        setInviteInfo(null);
        return;
      }
      const r = await lookupInvite(inviteCode);
      if (stop) return;
      if (r.ok) {
        setInviteInfo({ valid: true, ...r.data });
        setMode("signup"); // invite links always mean "sign up"
      } else {
        setInviteInfo({ valid: false, error: r.error });
      }
    })();
    return () => {
      stop = true;
    };
  }, [inviteCode, lookupInvite]);

  // First-time-approved members get sent to the welcome page automatically.
  React.useEffect(() => {
    if (!current || current.status !== "approved") return;
    const key = `tmm_welcomed_${current.id}`;
    try {
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        navigate("/welcome", { replace: true });
      }
    } catch {
      /* ignore storage issues */
    }
  }, [current, navigate]);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const updateForgot = (key) => (e) =>
    setForgot((f) => ({ ...f, [key]: e.target.value }));

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > AVATAR_MAX) {
      setErr("Please choose an image under 800 KB for the demo.");
      return;
    }
    const data = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, avatar: data }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await login(form.email, form.password);
        if (!r.ok) setErr(r.error);
        else window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!form.name.trim() || !form.email.trim() || !form.password) {
        setErr("Name, email and password are required.");
        return;
      }
      if (!form.security_answer.trim()) {
        setErr("Please answer your security question so you can reset the password later.");
        return;
      }
      const r = await register({ ...form, invite_code: inviteCode || undefined });
      if (!r.ok) {
        setErr(r.error);
        return;
      }
      // Clear the ?invite=… param — it's been consumed.
      if (searchParams.get("invite")) {
        const next = new URLSearchParams(searchParams);
        next.delete("invite");
        setSearchParams(next, { replace: true });
      }
      // Give the user visible confirmation that the account was created.
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setBusy(false);
    }
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (forgot.step === "email") {
        const r = await getSecurityQuestion(forgot.email.trim().toLowerCase());
        if (!r.ok) {
          setErr(r.error);
          return;
        }
        setForgot((f) => ({ ...f, question: r.data.security_question, step: "answer" }));
      } else if (forgot.step === "answer") {
        if (!forgot.answer.trim() || forgot.new_password.length < 6) {
          setErr("Answer your question and pick a new password of 6+ characters.");
          return;
        }
        const r = await resetPassword({
          email: forgot.email.trim().toLowerCase(),
          security_answer: forgot.answer,
          new_password: forgot.new_password,
        });
        if (!r.ok) {
          setErr(r.error);
          return;
        }
        setForgot({ email: "", question: "", answer: "", new_password: "", step: "done" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header hoverColor={pageColors.contact} compact={false} />
      <div className="members-page">
        <div className="members-hero members-hero-image">
          <img
            src="/img/members.png"
            alt="Members"
            className="members-hero-img"
            data-testid="members-hero-img"
          />
          <p className="members-sub">
            The Monde Mania community — sign up to unlock the creators room,
            member chat and downloads.
          </p>
        </div>

        {current ? (
          <div className="current-card" data-testid="current-card">
            <div className="current-avatar">
              {current.avatar || current.picture ? (
                <img src={current.avatar || current.picture} alt="" />
              ) : (
                <div className="current-avatar-fallback">
                  {(current.name || current.email)[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="current-info">
              <div className="current-hello">Signed in as</div>
              <div className="current-name">
                {current.name}
                {current.is_admin && <span className="admin-badge" style={{ marginLeft: 8 }}>ADMIN</span>}
              </div>
              <div className="current-expertise">{current.expertise || "member"}</div>

              {current.status === "pending" && (
                <div className="pending-banner" data-testid="pending-banner">
                  Your account is awaiting approval. An admin will let you in
                  soon — you can browse the site but chat, creators room, and
                  downloads unlock after approval.
                </div>
              )}

              <div className="current-actions">
                <button className="btn-solid" onClick={() => navigate(`/members/${current.id}`)} data-testid="view-profile-btn">
                  View / edit profile
                </button>
                {current.is_admin && (
                  <button className="btn-solid" onClick={() => navigate("/admin")} data-testid="admin-panel-btn">
                    Admin Panel
                  </button>
                )}
                {current.status === "approved" && (
                  <>
                    <button className="btn-solid" onClick={() => navigate("/the-shirt")} data-testid="the-shirt-btn">
                      <Shirt size={16} /> THE SHIRT — free for members
                    </button>
                    <button className="btn-solid" onClick={() => navigate("/welcome")} data-testid="welcome-btn">
                      Read the welcome
                    </button>
                    <button className="btn-ghost" onClick={() => navigate("/creators-room")}>
                      <Users size={16} /> Creators Room
                    </button>
                    <button className="btn-ghost" onClick={() => navigate("/news")}>
                      <Newspaper size={16} /> News Center
                    </button>
                    <button className="btn-ghost" onClick={() => navigate("/chat")}>
                      <MessageSquare size={16} /> Member Chat
                    </button>
                    <button className="btn-ghost" onClick={() => navigate("/availability")} data-testid="availability-link-btn">
                      <CalendarDays size={16} /> Availability Calendar
                    </button>
                    <button className="btn-ghost" onClick={() => navigate("/downloads")} data-testid="downloads-btn">
                      <Download size={16} /> Downloads
                    </button>
                  </>
                )}
                <button className="btn-ghost btn-danger" onClick={logout} data-testid="signout-btn">
                  <LogOut size={16} /> Sign out
                </button>
              </div>
              {current.status === "approved" && (
                <>
                  <MyBookingFee />
                  <PaidGigs />
                  <InviteFriends />
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="member-quick-links">
              <button className="btn-ghost" onClick={() => navigate("/creators-room")}>
                <Users size={16} /> Creators Room
              </button>
              <button className="btn-ghost" onClick={() => navigate("/news")}>
                <Newspaper size={16} /> News Center
              </button>
              <button className="btn-ghost" onClick={() => navigate("/chat")}>
                <MessageSquare size={16} /> Member Chat
              </button>
              <button className="btn-ghost" onClick={() => navigate("/availability")} data-testid="availability-quick-link">
                <CalendarDays size={16} /> Availability
              </button>
              <button className="btn-ghost" onClick={() => navigate("/downloads")}>
                <Download size={16} /> Downloads
              </button>
            </div>

            {resetToken && (
              <div className="auth-panel reset-token-panel" data-testid="reset-token-panel">
                <h2 className="auth-title"><KeyRound size={18} /> Set a new password</h2>
                <p className="auth-note">You followed a password reset link — pick a new password for your account below, then you'll be signed right in.</p>
                <form onSubmit={onTokenReset} data-testid="reset-token-form">
                  <label className="auth-field">
                    <span>New password</span>
                    <input type="password" value={rtPass} minLength={6} required
                      onChange={(e) => setRtPass(e.target.value)} data-testid="reset-token-password" />
                  </label>
                  <label className="auth-field">
                    <span>Confirm new password</span>
                    <input type="password" value={rtPass2} minLength={6} required
                      onChange={(e) => setRtPass2(e.target.value)} data-testid="reset-token-password2" />
                  </label>
                  {rtErr && <div className="auth-error" data-testid="reset-token-error">{rtErr}</div>}
                  <button type="submit" className="btn-solid" disabled={rtBusy} data-testid="reset-token-submit">
                    {rtBusy ? "…" : "Save new password"}
                  </button>
                </form>
              </div>
            )}

            <div className="auth-panel">
              {inviteInfo?.valid && (
                <div className="invite-banner" data-testid="invite-banner">
                  <Mail size={16} />
                  <span>
                    You were invited by <strong>{inviteInfo.inviter_name || inviteInfo.inviter_email}</strong>.
                    Finish signing up below — an admin will still need to approve your account.
                  </span>
                </div>
              )}
              {inviteInfo && !inviteInfo.valid && (
                <div className="invite-banner invite-banner-warn" data-testid="invite-banner-error">
                  <Mail size={16} />
                  <span>Invite link problem: {inviteInfo.error}</span>
                </div>
              )}
              <div className="no-fee-note" data-testid="no-fee-note">
                ✦ Membership is <strong>free forever</strong> — there is no fee to join The Monde Mania community.
              </div>
              {mode === "forgot" ? (
                <form className="auth-form" onSubmit={onForgotSubmit} noValidate data-testid="forgot-form">
                  <button
                    type="button"
                    className="auth-back"
                    onClick={() => {
                      setMode("login");
                      setErr("");
                      setForgot({ email: "", question: "", answer: "", new_password: "", step: "email" });
                    }}
                  >
                    <ArrowLeft size={14} /> Back to sign in
                  </button>
                  <h2 className="auth-h2">Reset your password</h2>

                  {forgot.step === "done" ? (
                    <div className="auth-success">
                      Password updated — you can sign in with your new password now.
                    </div>
                  ) : (
                    <>
                      <label className="auth-field">
                        <span>Email</span>
                        <input
                          type="email"
                          value={forgot.email}
                          onChange={updateForgot("email")}
                          placeholder="you@example.com"
                          disabled={forgot.step !== "email"}
                          data-testid="forgot-email"
                        />
                      </label>

                      {forgot.step === "answer" && (
                        <>
                          <div className="auth-question">
                            Security question:
                            <br />
                            <strong>{forgot.question}</strong>
                          </div>
                          <label className="auth-field">
                            <span>Your answer</span>
                            <input
                              value={forgot.answer}
                              onChange={updateForgot("answer")}
                              placeholder="Answer (not case-sensitive)"
                              data-testid="forgot-answer"
                            />
                          </label>
                          <label className="auth-field">
                            <span>New password</span>
                            <input
                              type="password"
                              value={forgot.new_password}
                              onChange={updateForgot("new_password")}
                              placeholder="At least 6 characters"
                              data-testid="forgot-new-password"
                            />
                          </label>
                        </>
                      )}

                      {err && <div className="auth-error" data-testid="forgot-error">{err}</div>}

                      <button type="submit" className="btn-solid" disabled={busy} data-testid="forgot-submit">
                        {forgot.step === "email" ? "Continue" : busy ? "…" : "Set new password"}
                      </button>

                      <div className="forgot-link-alt" data-testid="forgot-link-alt">
                        <div className="forgot-link-divider">Forgot your security answer too?</div>
                        {forgotLinkState === "sent" ? (
                          <div className="auth-success" data-testid="forgot-link-sent">
                            If an account exists for that email, a reset link is on its way — check your inbox.
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={forgotLinkState === "busy"}
                            onClick={emailResetLink}
                            data-testid="forgot-email-link-btn"
                          >
                            <Mail size={14} /> {forgotLinkState === "busy" ? "Sending…" : "Email me a reset link instead"}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </form>
              ) : (
                <>
                  <div className="auth-tabs">
                    <button
                      className={mode === "signup" ? "tab active" : "tab"}
                      onClick={() => { setMode("signup"); setErr(""); }}
                      data-testid="tab-signup"
                    >
                      <UserPlus size={16} /> Sign up
                    </button>
                    <button
                      className={mode === "login" ? "tab active" : "tab"}
                      onClick={() => { setMode("login"); setErr(""); }}
                      data-testid="tab-login"
                    >
                      <LogIn size={16} /> Log in
                    </button>
                  </div>

                  <form className="auth-form" onSubmit={onSubmit} noValidate>
                    {mode === "signup" && (
                      <div className="auth-row">
                        <label className="auth-field">
                          <span>Full name*</span>
                          <input value={form.name} onChange={update("name")} placeholder="Your name" data-testid="signup-name" />
                        </label>
                        <label className="auth-field">
                          <span>Expertise</span>
                          <input value={form.expertise} onChange={update("expertise")} placeholder="Painter, DJ, stylist…" data-testid="signup-expertise" />
                        </label>
                      </div>
                    )}
                    <div className="auth-row">
                      <label className="auth-field">
                        <span>Email*</span>
                        <input
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          placeholder="you@example.com"
                          data-testid={mode === "login" ? "login-email" : "signup-email"}
                        />
                      </label>
                      <label className="auth-field">
                        <span>Password*</span>
                        <input
                          type="password"
                          value={form.password}
                          onChange={update("password")}
                          placeholder={mode === "login" ? "Your password" : "Choose 6+ characters"}
                          data-testid={mode === "login" ? "login-password" : "signup-password"}
                        />
                      </label>
                    </div>

                    {mode === "signup" && (
                      <>
                        <div className="auth-row">
                          <label className="auth-field">
                            <span>Security question*</span>
                            <select
                              value={form.security_question}
                              onChange={update("security_question")}
                              data-testid="signup-question"
                            >
                              {SECURITY_QUESTIONS.map((q) => (
                                <option key={q} value={q}>{q}</option>
                              ))}
                            </select>
                          </label>
                          <label className="auth-field">
                            <span>Your answer*</span>
                            <input
                              value={form.security_answer}
                              onChange={update("security_answer")}
                              placeholder="Used to reset your password"
                              data-testid="signup-answer"
                            />
                          </label>
                        </div>

                        <div className="auth-row">
                          <label className="auth-field">
                            <span>Phone (optional)</span>
                            <input value={form.phone} onChange={update("phone")} placeholder="+1 555…" />
                          </label>
                          <label className="auth-field">
                            <span>Instagram (optional)</span>
                            <input value={form.instagram} onChange={update("instagram")} placeholder="@handle" />
                          </label>
                        </div>
                        <div className="auth-row">
                          <label className="auth-field">
                            <span>Other socials (optional)</span>
                            <input value={form.twitter} onChange={update("twitter")} placeholder="Twitter / TikTok / SoundCloud…" />
                          </label>
                          <label className="auth-field">
                            <span>Website (optional)</span>
                            <input value={form.website} onChange={update("website")} placeholder="https://…" />
                          </label>
                        </div>
                        <label className="auth-field">
                          <span>Quick intro (helps the admin approve you)</span>
                          <textarea
                            rows={3}
                            value={form.intro}
                            onChange={update("intro")}
                            placeholder="A sentence or two — who you are, what you're making, why you want in."
                            data-testid="signup-intro"
                          />
                        </label>
                        <label className="auth-field">
                          <span>Where are you based? (optional)</span>
                          <input
                            value={form.location}
                            onChange={update("location")}
                            placeholder="City, Country — e.g. Albuquerque, USA"
                            data-testid="signup-location"
                          />
                        </label>
                        <label className="auth-field">
                          <span>Bio</span>
                          <textarea rows={3} value={form.bio} onChange={update("bio")} placeholder="Tell the community what you do." />
                        </label>
                        <label className="auth-field">
                          <span>Profile picture</span>
                          <input type="file" accept="image/*" onChange={onAvatar} />
                          {form.avatar && <img src={form.avatar} alt="preview" className="avatar-preview" />}
                        </label>
                      </>
                    )}

                    {err && <div className="auth-error" data-testid="auth-error">{err}</div>}

                    <div className="auth-actions">
                      <button type="submit" className="btn-solid" disabled={busy} data-testid="auth-submit">
                        {busy ? "…" : mode === "signup" ? "Create account" : "Log in"}
                      </button>
                      {mode === "login" && (
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => { setMode("forgot"); setErr(""); }}
                          data-testid="forgot-password-btn"
                        >
                          <KeyRound size={14} /> Forgot password?
                        </button>
                      )}
                    </div>
                    <p className="auth-note">
                      Accounts and profiles are stored on our servers — your data
                      is safe across devices.
                    </p>
                  </form>
                </>
              )}
            </div>
          </>
        )}

        {current && current.status === "approved" && (
          <div className="avail-cta" data-testid="availability-cta">
            <button className="btn-solid" onClick={() => navigate("/availability")} data-testid="availability-cta-btn">
              <CalendarDays size={16} /> Open the Availability Calendar
            </button>
          </div>
        )}

        <section className="members-grid-section">
          <h2 className="members-grid-title">Members*</h2>
          <MembersMap
            members={users.filter((u) => u.status !== "pending")}
            canSeeCount={!!current?.can_see_member_count}
            accessStatus={current?.member_count_access_status || "none"}
            onRequestAccess={
              current && current.status === "approved" && !current.can_see_member_count && current.member_count_access_status !== "pending"
                ? async () => {
                    const r = await requestMemberCountAccess();
                    if (!r.ok) alert(r.error || "Could not send request.");
                  }
                : null
            }
          />
          {users.length === 0 ? (
            <div className="members-empty">No members yet — be the first to sign up.</div>
          ) : (
            <div className="members-grid">
              {users.map((u) => {
                const hl = highlights[u.id];
                return (
                <button
                  key={u.id}
                  type="button"
                  className={`member-card${hl ? " has-highlight" : ""}`}
                  onClick={() => navigate(`/members/${u.id}`)}
                  data-testid={`member-card-${u.id}`}
                >
                  {hl && (
                    <div className="member-card-highlight" data-testid={`member-highlight-${u.id}`}>
                      <img src={hl.main_image_url} alt={hl.title} />
                    </div>
                  )}
                  <div className="member-avatar">
                    {u.avatar || u.picture ? (
                      <img src={u.avatar || u.picture} alt={u.name} />
                    ) : (
                      <div className="member-avatar-fallback">
                        {(u.name || u.email)[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="member-name">{u.name}</div>
                  <div className="member-expertise">{u.expertise || "member"}</div>
                </button>
              );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default Members;
